import React from 'react';
import { MessageSquare } from 'lucide-react';
import { RobbieMark } from '../../ui';
import { OutreachAction, SupplierThread } from './types';
import ActionCard from './ActionCard';
import KickoffDrafting from './KickoffDrafting';
import { useReadOnly } from '../../../contexts/WizardAccessContext';

/**
 * What a conversation looks like before it is one.
 *
 * A thread nobody has written to has no messages to fill the pane and no
 * history to read, so the space goes to the thing the buyer is actually here
 * to do: Robbie's opening asks, at full size, each one a click away from the
 * composer below. The compact moves bar is for a live conversation, where the
 * messages need the height more than the moves do.
 *
 * Three honest states and no fourth. Asks ready → the cards. Nothing yet but
 * the server says Robbie is still working → one drafting line, bounded by a
 * timeout that turns into an error with a retry.
 * Nothing, and the run has finished → say that, and point at the composer.
 * Nothing is ever invented to fill the space.
 *
 * A viewer reads the asks and sends none of them — the cards drop their
 * controls and the copy stops pointing at a composer that is not drawn.
 */
interface EmptyThreadBodyProps {
  thread: SupplierThread;
  actions: OutreachAction[];
  /** The server's word that opening asks are still being written. */
  kickoffPending: boolean;
  /** The kickoff has been pending past its timeout. */
  kickoffTimedOut?: boolean;
  onRetryKickoff?: () => void;
  /** Ids whose asks are already in the composer, waiting on the buyer's send. */
  stagedIds: string[];
  onStage: (actions: OutreachAction[]) => void;
  onDismiss: (actionId: string) => void;
  busy?: boolean;
}

const EmptyThreadBody: React.FC<EmptyThreadBodyProps> = ({
  thread,
  actions,
  kickoffPending,
  kickoffTimedOut = false,
  onRetryKickoff,
  stagedIds,
  onStage,
  onDismiss,
  busy,
}) => {
  const readOnly = useReadOnly();

  return (
  <div className="flex flex-col gap-s4">
    <div className="flex items-start gap-s3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-round bg-neutral-bg">
        <MessageSquare className="h-[18px] w-[18px] text-ink-muted" />
      </span>
      <div className="min-w-0">
        <p className="m-0 text-row-title text-ink">No messages yet</p>
        <p className="m-0 mt-s1 max-w-md text-body-sm text-ink-muted">
          {readOnly
            ? `Nothing has gone to ${thread.supplierName} yet.`
            : `Tell me what to ask ${thread.supplierName} in the box below, or take one of my asks — I write the email either way.`}
        </p>
      </div>
    </div>

    {actions.length > 0 ? (
      <div className="flex flex-col gap-s3">
        <div className="flex items-center gap-s2">
          <RobbieMark size="xxs" aria-label="Robbie's opening asks" />
          <span className="text-meta font-bold text-ink">
            What I&rsquo;d open with · {actions.length} ask{actions.length === 1 ? '' : 's'}
          </span>
          <span className="text-[10.5px] text-ink-muted">
            {readOnly
              ? "Robbie's opening asks — sending them is the RFQ owner's call."
              : 'Nothing here goes out — an ask lands in your box and waits for your send.'}
          </span>
        </div>
        {actions.map((action) => (
          <ActionCard
            key={action.id}
            action={action}
            staged={stagedIds.includes(action.id)}
            disabled={busy}
            onStage={() => onStage([action])}
            onDismiss={() => onDismiss(action.id)}
          />
        ))}
      </div>
    ) : kickoffPending ? (
      <KickoffDrafting supplierName={thread.supplierName} timedOut={kickoffTimedOut} onRetry={onRetryKickoff} />
    ) : (
      <div className="flex items-center gap-s2">
        <RobbieMark size="xxs" aria-label="Robbie has nothing to suggest here" />
        <span className="text-meta leading-[1.4] text-ink-secondary">
          {readOnly ? 'Nothing here needs anyone yet.' : 'Nothing here needs you yet — write your own ask below.'}
        </span>
      </div>
    )}
  </div>
  );
};

export default EmptyThreadBody;
